/**
 * Dashboard Component
 * Tab 1: Home screen with allergy summary, emergency plan, recent activity
 * Mobile-first responsive design
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, TrendingUp, Activity } from 'lucide-react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import useAuth from '../../hooks/useAuth';
import useAPI from '../../hooks/useAPI';

function Dashboard({ onOpenReactions }) {
  const { user } = useAuth();
  const { get } = useAPI();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Allergen names mapping
  const ALLERGEN_NAMES = {
    1: 'Milk',
    2: 'Eggs',
    3: 'Peanuts',
    4: 'Tree Nuts',
    5: 'Shellfish',
    6: 'Fish',
    7: 'Wheat',
    8: 'Soy',
    9: 'Sesame',
    10: 'Mustard',
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const data = await get('/api/reactions/analytics');
        setAnalytics(data);
      } catch (err) {
        setError('Failed to load analytics');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [get]);

  return (
    <div className="dashboard-container">
      {/* Allergy Profile Section */}
      <Card className="allergy-profile-card">
        <h2 className="card-title">Your Allergies</h2>
        {user?.allergies && user.allergies.length > 0 ? (
          <div className="allergy-badges">
            {user.allergies.map((allergyId) => (
              <Badge key={allergyId} variant="danger" size="lg">
                {ALLERGEN_NAMES[allergyId] || `Allergen ${allergyId}`}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-muted">No allergies set. Update your profile.</p>
        )}
      </Card>

      {/* Emergency Action Plan Section */}
      <Card className="emergency-card">
        <div className="emergency-header">
          <AlertCircle className="icon-danger" size={24} />
          <h2 className="card-title">Emergency Action Plan</h2>
        </div>
        <div className="emergency-content">
          <p className="emergency-subtitle">In case of allergic reaction:</p>
          <ol className="emergency-steps">
            <li>
              <strong>Stop eating</strong> - Do not consume more of the food
            </li>
            <li>
              <strong>Assess symptoms</strong> - Check for itching, swelling, hives
            </li>
            <li>
              <strong>Administer EpiPen</strong> - If prescribed and symptoms develop
            </li>
            <li>
              <strong>Call 911</strong> - Always call emergency services
            </li>
            <li>
              <strong>Report to app</strong> - Log reaction for future reference
            </li>
          </ol>
          <button className="btn btn-danger btn-full btn-lg">View Full Action Plan</button>
        </div>
      </Card>

      {/* Recent Activity & Analytics */}
      {loading ? (
        <div className="loading-spinner">Loading analytics...</div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : analytics ? (
        <>
          <Card className="analytics-card">
            <h3 className="card-title">Reaction History</h3>
            <div className="stat-grid">
              <div className="stat-item">
                <Activity size={20} className="stat-icon" />
                <div className="stat-content">
                  <p className="stat-label">Total Reactions</p>
                  <p className="stat-value">{analytics.total_reactions}</p>
                </div>
              </div>
              <div className="stat-item">
                <TrendingUp size={20} className="stat-icon" />
                <div className="stat-content">
                  <p className="stat-label">Avg. Severity</p>
                  <p className="stat-value">{analytics.average_severity}</p>
                </div>
              </div>
            </div>

            {/* Severity Distribution */}
            {analytics.severity_distribution && (
              <div className="severity-breakdown">
                <p className="section-label">Severity Breakdown</p>
                <div className="severity-bars">
                  <div className="severity-bar">
                    <span className="bar-label">Mild</span>
                    <div className="bar-container">
                      <div className="bar bar-green" style={{ width: `${(analytics.severity_distribution.mild / analytics.total_reactions) * 100}%` }} />
                    </div>
                    <span className="bar-count">{analytics.severity_distribution.mild}</span>
                  </div>
                  <div className="severity-bar">
                    <span className="bar-label">Moderate</span>
                    <div className="bar-container">
                      <div className="bar bar-yellow" style={{ width: `${(analytics.severity_distribution.moderate / analytics.total_reactions) * 100}%` }} />
                    </div>
                    <span className="bar-count">{analytics.severity_distribution.moderate}</span>
                  </div>
                  <div className="severity-bar">
                    <span className="bar-label">Severe</span>
                    <div className="bar-container">
                      <div className="bar bar-red" style={{ width: `${(analytics.severity_distribution.severe / analytics.total_reactions) * 100}%` }} />
                    </div>
                    <span className="bar-count">{analytics.severity_distribution.severe}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Most Common Symptoms */}
            {analytics.most_common_symptoms && analytics.most_common_symptoms.length > 0 && (
              <div className="common-symptoms">
                <p className="section-label">Most Common Symptoms</p>
                <ul className="symptom-list">
                  {analytics.most_common_symptoms.map((symptom) => (
                    <li key={symptom.name}>
                      <span className="symptom-name">{symptom.name.replace(/_/g, ' ')}</span>
                      <span className="symptom-count">{symptom.count}x</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button className="btn btn-secondary btn-full dashboard-reactions-link" onClick={onOpenReactions}>
              View &amp; log reactions
            </button>
          </Card>
        </>
      ) : (
        <Card className="analytics-card">
          <h3 className="card-title">Reaction History</h3>
          <p className="text-muted">No reactions logged yet. Keeping a record helps you and your allergist spot patterns.</p>
          <button className="btn btn-primary btn-full dashboard-reactions-link" onClick={onOpenReactions}>
            Log a reaction
          </button>
        </Card>
      )}

      {/* Quick Tips */}
      <Card className="tips-card">
        <h3 className="card-title">📚 Quick Tip</h3>
        <p className="tip-text">
          Always read the label, even if you've bought a product before. Ingredients change frequently without warning. When dining out, clearly communicate your allergies to the server and ask about preparation methods.
        </p>
      </Card>
    </div>
  );
}

export default Dashboard;
